/**
 * Browser Storage Adapter
 * Implements storage using FileSystem API with IndexedDB fallback
 */

import type {
  IStorageAdapter,
  StorageCapabilities,
  StorageFile,
  StorageFilter,
  SearchResult,
  StorageError,
} from './types';
import { nanoid } from 'nanoid';
import lunr from 'lunr';

export class BrowserStorageAdapter implements IStorageAdapter {
  private db: IDBDatabase | null = null;
  private searchIndex: lunr.Index | null = null;
  private fileSystemHandle: FileSystemDirectoryHandle | null = null;
  private hasFileSystemAccess = false;

  getCapabilities(): StorageCapabilities {
    const hasFileSystemAPI = 'showDirectoryPicker' in window;
    return {
      canRequestDirectory: hasFileSystemAPI,
      canPersistHandles: hasFileSystemAPI,
      supportsFileSystemAPI: hasFileSystemAPI,
      supportsIndexedDB: true,
      supportsTauri: false,
      maxFileSize: 50 * 1024 * 1024, // 50MB
      supportedFileTypes: ['text/plain', 'text/markdown', 'application/json'],
    };
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Check IndexedDB availability (always our fallback)
      const testDB = await this.openDatabase();
      testDB.close();
      return true;
    } catch {
      return false;
    }
  }

  async hasPermission(): Promise<boolean> {
    if (!this.fileSystemHandle) return true; // IndexedDB doesn't need permission

    try {
      // Check if queryPermission is available (it's not in all browsers)
      if ('queryPermission' in this.fileSystemHandle) {
        const permission = await (this.fileSystemHandle as any).queryPermission({
          mode: 'readwrite',
        });
        return permission === 'granted';
      }

      // Fallback: try to create a test file
      try {
        if (!this.fileSystemHandle) return false;
        const handle = this.fileSystemHandle as FileSystemDirectoryHandle;
        await handle.getFileHandle('.phrasewright-test', { create: true });
        await handle.removeEntry('.phrasewright-test');
        return true;
      } catch {
        return false;
      }
    } catch {
      return true; // If we can't check, assume we have permission (IndexedDB)
    }
  }

  async requestPermission(): Promise<boolean> {
    const capabilities = this.getCapabilities();

    if (capabilities.canRequestDirectory) {
      try {
        this.fileSystemHandle = await window.showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents',
        });

        this.hasFileSystemAccess = true;

        // Persist the handle for future sessions
        await this.persistFileSystemHandle(this.fileSystemHandle);

        return true;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return false;
        } else if (error instanceof DOMException) {
          if (error.name === 'NotAllowedError') {
            throw new Error('Permission denied. Please allow file system access when prompted.');
          } else if (error.name === 'SecurityError') {
            throw new Error(
              'Security restrictions prevent file system access. This may be due to browser settings or the current context.'
            );
          }
        }

        throw new Error(
          `Failed to access file system: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }

    // Fall back to IndexedDB (no permission needed)
    return true;
  }

  async initialize(): Promise<void> {
    // Open IndexedDB
    this.db = await this.openDatabase();

    // Try to restore FileSystem handle from previous session
    await this.restoreFileSystemHandle();

    // Build search index
    await this.rebuildSearchIndex();
  }

  async cleanup(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.searchIndex = null;
    this.fileSystemHandle = null;
  }

  // CRUD Operations
  async getFile(id: string): Promise<StorageFile> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readonly');
      const request = transaction.objectStore('files').get(id);

      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          const error: StorageError = Object.assign(new Error(`File not found: ${id}`), {
            code: 'NOT_FOUND' as const,
            retryable: false,
            fallbackAvailable: false,
          });
          reject(error);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async getFiles(filter?: StorageFilter): Promise<StorageFile[]> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readonly');
      const request = transaction.objectStore('files').getAll();

      request.onsuccess = () => {
        let files = request.result || [];

        // Apply filters
        if (filter) {
          if (filter.projectId) {
            files = files.filter((f) => f.projectId === filter.projectId);
          }
          if (filter.type) {
            files = files.filter((f) => f.type === filter.type);
          }
          if (filter.tags?.length) {
            files = files.filter((f) => filter.tags!.some((tag) => f.tags.includes(tag)));
          }
          if (filter.createdAfter) {
            files = files.filter((f) => f.createdAt > filter.createdAfter!);
          }
          if (filter.updatedAfter) {
            files = files.filter((f) => f.updatedAt > filter.updatedAfter!);
          }
        }

        resolve(files);
      };

      request.onerror = () => reject(request.error);
    });
  }

  async createFile(
    file: Omit<StorageFile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<StorageFile> {
    const db = await this.ensureDatabase();

    const newFile: StorageFile = {
      ...file,
      id: nanoid(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      revision: 1,
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readwrite');
      const request = transaction.objectStore('files').add(newFile);

      request.onsuccess = async () => {
        // Update search index
        await this.addToSearchIndex(newFile);

        // If we have FileSystem access, also save there
        if (this.hasFileSystemAccess && this.fileSystemHandle) {
          await this.saveToFileSystem(newFile);
        }

        resolve(newFile);
      };

      request.onerror = () => reject(request.error);
    });
  }

  async updateFile(id: string, updates: Partial<StorageFile>): Promise<StorageFile> {
    const db = await this.ensureDatabase();
    const existingFile = await this.getFile(id);

    const updatedFile: StorageFile = {
      ...existingFile,
      ...updates,
      id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString(),
      revision: (existingFile.revision || 0) + 1,
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readwrite');
      const request = transaction.objectStore('files').put(updatedFile);

      request.onsuccess = async () => {
        // Update search index
        await this.rebuildSearchIndex();

        // If we have FileSystem access, also update there
        if (this.hasFileSystemAccess && this.fileSystemHandle) {
          await this.saveToFileSystem(updatedFile);
        }

        resolve(updatedFile);
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteFile(id: string): Promise<void> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['files'], 'readwrite');
      const request = transaction.objectStore('files').delete(id);

      request.onsuccess = async () => {
        // Update search index
        await this.rebuildSearchIndex();

        // If we have FileSystem access, also delete there
        if (this.hasFileSystemAccess && this.fileSystemHandle) {
          await this.deleteFromFileSystem(id);
        }

        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Batch operations
  async createFiles(
    files: Array<Omit<StorageFile, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<StorageFile[]> {
    const results: StorageFile[] = [];
    for (const file of files) {
      results.push(await this.createFile(file));
    }
    return results;
  }

  async updateFiles(
    updates: Array<{ id: string; updates: Partial<StorageFile> }>
  ): Promise<StorageFile[]> {
    const results: StorageFile[] = [];
    for (const update of updates) {
      results.push(await this.updateFile(update.id, update.updates));
    }
    return results;
  }

  async deleteFiles(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.deleteFile(id);
    }
  }

  // Search
  async search(
    query: string,
    options?: { limit?: number; offset?: number }
  ): Promise<SearchResult[]> {
    if (!this.searchIndex) {
      await this.rebuildSearchIndex();
    }

    if (!this.searchIndex) {
      return [];
    }

    const results = this.searchIndex.search(query);
    const limit = options?.limit || 20;
    const offset = options?.offset || 0;

    const searchResults: SearchResult[] = [];

    for (let i = offset; i < Math.min(results.length, offset + limit); i++) {
      const result = results[i];
      const file = await this.getFile(result.ref);

      searchResults.push({
        fileId: file.id,
        fileName: file.name,
        excerpt: this.generateExcerpt(file.content, query),
        score: result.score,
      });
    }

    return searchResults;
  }

  async rebuildSearchIndex(): Promise<void> {
    const files = await this.getFiles();

    this.searchIndex = lunr(function () {
      this.ref('id');
      this.field('name', { boost: 10 });
      this.field('content');
      this.field('tags', { boost: 5 });

      files.forEach((file) => {
        this.add({
          id: file.id,
          name: file.name,
          content: file.content,
          tags: file.tags.join(' '),
        });
      });
    });
  }

  // Export/Import
  async exportData(): Promise<Blob> {
    const files = await this.getFiles();
    const data = JSON.stringify({ files, version: '1.0.0' });
    return new Blob([data], { type: 'application/json' });
  }

  async importData(data: Blob): Promise<void> {
    const text = await data.text();
    const { files } = JSON.parse(text);

    for (const file of files) {
      await this.createFile(file);
    }
  }

  // Private helper methods
  private async ensureDatabase(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = await this.openDatabase();
    }
    return this.db;
  }

  private async openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('phrasewright-storage', 2);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('files')) {
          const store = db.createObjectStore('files', { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
          store.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        if (!db.objectStoreNames.contains('handles')) {
          db.createObjectStore('handles');
        }
      };
    });
  }

  private async persistFileSystemHandle(handle: FileSystemDirectoryHandle): Promise<void> {
    const db = await this.ensureDatabase();

    return new Promise((resolve, _reject) => {
      const transaction = db.transaction(['handles'], 'readwrite');
      const request = transaction.objectStore('handles').put(handle, 'directory');

      request.onsuccess = () => resolve();
      request.onerror = () => {
        resolve(); // Don't fail, just warn
      };
    });
  }

  private async restoreFileSystemHandle(): Promise<void> {
    const db = await this.ensureDatabase();

    return new Promise((resolve) => {
      const transaction = db.transaction(['handles'], 'readonly');
      const request = transaction.objectStore('handles').get('directory');

      request.onsuccess = async () => {
        if (request.result) {
          this.fileSystemHandle = request.result as FileSystemDirectoryHandle;
          this.hasFileSystemAccess = await this.hasPermission();
        }
        resolve();
      };

      request.onerror = () => {
        resolve(); // Don't fail, just warn
      };
    });
  }

  private async saveToFileSystem(file: StorageFile): Promise<void> {
    if (!this.fileSystemHandle) return;

    try {
      const fileName = `${file.id}.json`;
      const fileHandle = await this.fileSystemHandle.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();

      await writable.write(JSON.stringify(file, null, 2));
      await writable.close();
    } catch (error) {
      // Don't throw - IndexedDB is our source of truth
    }
  }

  private async deleteFromFileSystem(id: string): Promise<void> {
    if (!this.fileSystemHandle) return;

    try {
      const fileName = `${id}.json`;
      await this.fileSystemHandle.removeEntry(fileName);
    } catch (error) {
      // Don't throw - IndexedDB is our source of truth
    }
  }

  private async addToSearchIndex(_file: StorageFile): Promise<void> {
    if (!this.searchIndex) {
      await this.rebuildSearchIndex();
      return;
    }

    // Lunr doesn't support dynamic updates, so we rebuild
    await this.rebuildSearchIndex();
  }

  private generateExcerpt(content: string, query: string): string {
    const queryLower = query.toLowerCase();
    const contentLower = content.toLowerCase();
    const index = contentLower.indexOf(queryLower);

    if (index === -1) {
      return content.substring(0, 150) + '...';
    }

    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + query.length + 100);

    let excerpt = content.substring(start, end);
    if (start > 0) excerpt = '...' + excerpt;
    if (end < content.length) excerpt = excerpt + '...';

    return excerpt;
  }
}
